import { createSelector } from 'reselect';

import moment from 'moment';

import find from 'lodash/find';
import memoize from 'lodash/memoize';
import curry from 'lodash/curry';
import get from 'lodash/get';
import prop from 'lodash/fp/prop';
import pipe from 'lodash/fp/pipe';
import filter from 'lodash/fp/filter';
import orderBy from 'lodash/fp/orderBy';
import groupBy from 'lodash/fp/groupBy';
import keyBy from 'lodash/fp/keyBy';
import map from 'lodash/fp/map';
import some from 'lodash/fp/some';
import omit from 'lodash/fp/omit';
import mapValues from 'lodash/fp/mapValues';
import pickBy from 'lodash/fp/pickBy';
import fifaBestThirdMatrix from '@constants/fifa-third-place-matrix.json';

import {
  Stage,
  Bracket as BracketType,
  Round,
  MatchProps
} from '@models/Match';

import {
  mergeMatches,
  mergeMatchesKeyed,
  mergeMatchesScoresKeyed,
  resolveTeam,
  getTeamPoints,
  allMatchesPlayed,
  getTeams,
  mergeAssertions,
  hasResults,
} from '@utils/match';
import {
  getStartRoundForFase,
  isRoundAtOrAfter,
  isRoundBefore,
  isRoundInWildcardScope,
  getComodinStartRound,
  isRoundAfter,
  stripStaleForecastAfterComodin,
  stripForecastTeamsForPropagation,
  ROUND_ORDER,
} from '@utils/fase';
import { canActivateComodin, hasComodinesAvailable } from '@utils/comodines';
import { byFairPlay } from '@utils/fair-play';

import { isShareForecast, isEditingForecast } from '@selectors/location';
import { isPlayerDirty } from '@selectors/players';
import { isWordCupStarted, isBracketStageStarted } from '@selectors/clock';

// const log = (what, showData) => data => {
//   console.log(what);
//   if (showData) console.log(data);
//   return data;
// }

const matchPreDeadline = deadlineTime =>
  memoize(datetime => new Date(datetime).getTime() > deadlineTime);

const setEditableMatches = curry((now, deadline, deadlineUnit, matches) => {
  let isPreDeadline = matchPreDeadline(
    moment(now)
      .add(deadline, deadlineUnit)
      .valueOf()
  );

  return matches.map(match => ({
    ...match,
    editable: isPreDeadline(match.datetime)
  }));
});

export const selectMatches = createSelector(
  [prop('now'), prop('config'), prop('matches')],
  (now, { deadline, deadlineUnit = 'hours' } = {}, matches) =>
    pipe(
      setEditableMatches(now, deadline, deadlineUnit),
      orderBy([MatchProps.Datetime, MatchProps.MatchId], ['asc', 'asc'])
    )(matches)
);

const createSelectorByStage = stage =>
  createSelector(selectMatches, filter({ stage }));
const selectGroupStage = createSelectorByStage(Stage.Groups);
const selectBracketStage = createSelectorByStage(Stage.Bracket);

const addIsToday = (now) => (match) => ({ 
  ...match,
  isToday: moment(match[MatchProps.Datetime]).isSame(now, 'day'),
});

export const selectGroupMatches = createSelector(
  [selectGroupStage, prop('now'), prop('forecastMatches')],
  (matches, now, forecastMatches) =>
    pipe(
      map(addIsToday(now)),
      mergeAssertions(forecastMatches || []),
      groupBy(MatchProps.Group)
    )(matches)
);

export const selectTeamGroups = createSelector(
  selectGroupMatches,
  mapValues(getTeams)
);

export const selectTeamGroupByCode = createSelector(
  selectTeamGroups,
  (teamsByGroup = {}) =>
    Object.keys(teamsByGroup).reduce((all, group) => {
      (teamsByGroup[group] || []).forEach((team) => {
        if (!team) return;
        all[String(team).toLowerCase()] = group;
      });
      return all;
    }, {})
);

export const isWildcardModeEnabled = (state) =>
  get(state, 'wildcardMode.enabled', false);

export const isKnockoutEditPermitEnabled = (state) =>
  get(state, 'user.knockoutEditPermit.enabled', false) === true &&
  (Number(get(state, 'user.knockoutEditPermit.remainingUses', 0)) || 0) > 0;

export const canActivateWildcardMode = createSelector(
  [prop('user'), prop('torneoEstado')],
  (user, torneoEstado) => canActivateComodin(user, torneoEstado)
);

export { hasComodinesAvailable };

export const canEditForecast = createSelector(
  [isShareForecast, isWordCupStarted, isWildcardModeEnabled, isKnockoutEditPermitEnabled],
  (isShareMode, worldCupStarted, wildcardModeEnabled, knockoutEditPermitEnabled) => {
    if (isShareMode) return false;
    if (!worldCupStarted) return true;
    return wildcardModeEnabled || knockoutEditPermitEnabled;
  }
);
export const isForecastView = state => get(state, 'subpage') === 'forecast';

export const selectGroupForecast = createSelector(
  [selectGroupStage, prop('forecastMatches'), prop('now')],
  (matches, forecast, now) =>
    pipe(
      map(
        omit([MatchProps.ScoreHome, MatchProps.ScoreAway, MatchProps.Winner])
      ),
      map(addIsToday(now)),
      mergeMatches(forecast),
      groupBy(MatchProps.Group)
    )(matches)
);

const selectFifaRankingByTeamCode = (state) =>
  get(state, 'fifaRanking.byTeamCode', {});

const WC2026_R32_LEFT = [73, 75, 76, 78, 79, 80, 81, 82];
const WC2026_R32_RIGHT = [74, 77, 83, 84, 85, 86, 87, 88];
// Keep R16 sides aligned with their quarterfinal branches:
// - Forward side feeds QF 97 and 98  (R16: 89, 90, 93, 94)
// - Reverse side feeds QF 99 and 100 (R16: 91, 92, 95, 96)
const WC2026_R16_LEFT = [89, 90, 93, 94];
const WC2026_R16_RIGHT = [91, 92, 95, 96];

const pickMids = (matches, mids) =>
  mids.map((mid) => matches[String(mid)] ?? matches[mid]);

const WC2026_FIRST_KNOCKOUT_MID = 73;

const hasRoundOf32Bracket = (matches) =>
  Object.values(matches || {}).some((m) => m && m.round === Round.Roundof32);

/** True if knockout uses FIFA 2026 match ids (R32 starts at 73). */
const hasWc2026BracketMids = (matches) =>
  Object.values(matches || {}).some(
    (m) => m && Number(m.mid) >= WC2026_FIRST_KNOCKOUT_MID
  );

/** WC2026 bracket *shape* (mids 73+ / roundof32) vs Qatar legacy. Layout UI is always BracketGrid on desktop. */
const shouldUseWc2026BracketLayout = (matches, config) => {
  const flag = config?.useWc2026BracketLayout;
  if (flag === false || flag === 'false') return false;
  if (flag === true || flag === 'true') return true;
  return hasRoundOf32Bracket(matches) || hasWc2026BracketMids(matches);
};

const getBracketRoundsLegacy = (matches) => ({
  [BracketType.Forward]: {
    [Round.Roundof16]: [
      matches['49'],
      matches['50'],
      matches['53'],
      matches['54']
    ],
    [Round.Quarter]: [matches['57'], matches['58']],
    [Round.Semi]: [matches['61']]
  },
  [BracketType.Final]: [matches['64'], matches['63']],
  [BracketType.Reverse]: {
    [Round.Roundof16]: [
      matches['51'],
      matches['52'],
      matches['55'],
      matches['56']
    ],
    [Round.Quarter]: [matches['59'], matches['60']],
    [Round.Semi]: [matches['62']]
  }
});

const getBracketRoundsWc2026 = (matches) => ({
  [BracketType.Forward]: {
    [Round.Roundof32]: pickMids(matches, WC2026_R32_LEFT),
    [Round.Roundof16]: pickMids(matches, WC2026_R16_LEFT),
    [Round.Quarter]: [matches['97'], matches['98']],
    [Round.Semi]: [matches['101']]
  },
  [BracketType.Final]: [matches['104'], matches['103']],
  [BracketType.Reverse]: {
    [Round.Roundof32]: pickMids(matches, WC2026_R32_RIGHT),
    [Round.Roundof16]: pickMids(matches, WC2026_R16_RIGHT),
    [Round.Quarter]: [matches['99'], matches['100']],
    [Round.Semi]: [matches['102']]
  }
});

export const getBracketRounds = (matches, config) =>
  shouldUseWc2026BracketLayout(matches, config)
    ? getBracketRoundsWc2026(matches)
    : getBracketRoundsLegacy(matches);

const getFifaRank = (byTeamCode, teamCode) =>
  get(byTeamCode, [String(teamCode || '').trim().toLowerCase()], Number.MAX_SAFE_INTEGER);

const byFifaRanking = (byTeamCode) => (a, b) =>
  getFifaRank(byTeamCode, a && a.team) - getFifaRank(byTeamCode, b && b.team);

const buildTableEntries = (pointsByTeam) =>
  Object.keys(pointsByTeam).map((team) => ({
    team,
    points: pointsByTeam[team][0],
    gdif: pointsByTeam[team][1],
    gf: pointsByTeam[team][2]
  }));

const byOverall = (byTeamCode, groupMatches) => (a, b) =>
  b.gdif - a.gdif ||
  b.gf - a.gf ||
  byFairPlay(groupMatches)(a, b) ||
  byFifaRanking(byTeamCode)(a, b) ||
  String(a.team).localeCompare(String(b.team));

const clusterBy = (entries, equals) =>
  entries.reduce((clusters, entry) => {
    const last = clusters[clusters.length - 1];
    if (last && equals(last[0], entry)) last.push(entry);
    else clusters.push([entry]);
    return clusters;
  }, []);

const rankTiedTeams = (tied, groupMatches, byTeamCode) => {
  if (tied.length <= 1) return tied;

  const codes = tied.map((e) => e.team);
  const h2hMatches = (groupMatches || []).filter(
    (m) => codes.indexOf(m.teamHome) !== -1 && codes.indexOf(m.teamAway) !== -1
  );
  const h2hPoints = getTeamPoints(h2hMatches);
  const h2h = {};
  codes.forEach((c) => {
    const p = h2hPoints[c] || [0, 0, 0];
    h2h[c] = { points: p[0], gdif: p[1], gf: p[2] };
  });

  const sorted = tied.slice().sort(
    (a, b) =>
      h2h[b.team].points - h2h[a.team].points ||
      h2h[b.team].gdif - h2h[a.team].gdif ||
      h2h[b.team].gf - h2h[a.team].gf ||
      byFairPlay(groupMatches)(a, b) ||
      byFifaRanking(byTeamCode)(a, b)
  );

  const sameH2h = (a, b) =>
    h2h[a.team].points === h2h[b.team].points &&
    h2h[a.team].gdif === h2h[b.team].gdif &&
    h2h[a.team].gf === h2h[b.team].gf;

  return clusterBy(sorted, sameH2h).reduce((acc, cluster) => {
    if (cluster.length === 1) return acc.concat(cluster);
    if (cluster.length === tied.length) {
      return acc.concat(cluster.slice().sort(byOverall(byTeamCode, groupMatches)));
    }
    return acc.concat(rankTiedTeams(cluster, groupMatches, byTeamCode));
  }, []);
};

const sortGroupTable = (pointsByTeam, groupMatches, byTeamCode) => {
  const byPoints = buildTableEntries(pointsByTeam).sort(
    (a, b) => b.points - a.points
  );
  return clusterBy(byPoints, (a, b) => a.points === b.points).reduce(
    (acc, cluster) => acc.concat(rankTiedTeams(cluster, groupMatches, byTeamCode)),
    []
  );
};

const getGroupQualifiers = (groups, byTeamCode) =>
  Object.keys(groups).reduce(
    (all, group) => {
      const groupMatches = groups[group] || [];
      if (!allMatchesPlayed(groupMatches)) {
        return {
          ...all,
          positionsByGroup: {
            ...all.positionsByGroup,
            [group]: []
          }
        };
      }

      const table = sortGroupTable(getTeamPoints(groupMatches), groupMatches, byTeamCode);
      const topTeams = table.slice(0, 3).map((entry) => entry.team);
      const third = table[2];

      return {
        positionsByGroup: {
          ...all.positionsByGroup,
          [group]: topTeams
        },
        bestThirds: third
          ? [...all.bestThirds, { ...third, group }]
          : all.bestThirds
      };
    },
    {
      positionsByGroup: {},
      bestThirds: []
    }
  );

const sortBestThirds = (bestThirds, byTeamCode, groupMatches) =>
  [...bestThirds].sort(
    (a, b) =>
      b.points - a.points ||
      b.gdif - a.gdif ||
      b.gf - a.gf ||
      byFairPlay(groupMatches)(a, b) ||
      byFifaRanking(byTeamCode)(a, b) ||
      String(a.team).localeCompare(String(b.team))
  );

const flattenGroupMatches = (groups) =>
  Object.keys(groups).reduce(
    (all, group) => [...all, ...(groups[group] || [])],
    []
  );

const withSortedBestThirds = (qualifiers, byTeamCode, groupMatches) => ({
  ...qualifiers,
  bestThirds: sortBestThirds(qualifiers.bestThirds || [], byTeamCode, groupMatches)
});

const selectGroupTopTwo = (positionsByGroup) =>
  Object.keys(positionsByGroup).reduce(
    (all, group) => ({
      ...all,
      [group]: (positionsByGroup[group] || []).slice(0, 2)
    }),
    {}
  );

export const selectGroupWinners = createSelector(
  [selectGroupMatches, selectFifaRankingByTeamCode],
  (groups, byTeamCode) =>
    pipe(
      (value) => getGroupQualifiers(value, byTeamCode),
      prop('positionsByGroup'),
      selectGroupTopTwo
    )(groups)
);

export const selectGroupWinnersForecast = createSelector(
  [selectGroupForecast, selectFifaRankingByTeamCode],
  (matches, byTeamCode) =>
    pipe(
      (value) => getGroupQualifiers(value, byTeamCode),
      prop('positionsByGroup'),
      selectGroupTopTwo
    )(matches)
);

const selectGroupQualifiers = createSelector(
  [selectGroupMatches, selectFifaRankingByTeamCode],
  (matches, byTeamCode) => {
    const allGroupMatches = flattenGroupMatches(matches);
    return pipe(
      (value) => getGroupQualifiers(value, byTeamCode),
      (qualifiers) => withSortedBestThirds(qualifiers, byTeamCode, allGroupMatches)
    )(matches);
  }
);

const selectGroupQualifiersForecast = createSelector(
  [selectGroupForecast, selectFifaRankingByTeamCode],
  (matches, byTeamCode) => {
    const allGroupMatches = flattenGroupMatches(matches);
    return pipe(
      (value) => getGroupQualifiers(value, byTeamCode),
      (qualifiers) => withSortedBestThirds(qualifiers, byTeamCode, allGroupMatches)
    )(matches);
  }
);

const getTeamFromGroup = (positionsByGroup, from) => {
  if (!from || from.group == null) return undefined;
  const arr = positionsByGroup[from.group];
  if (!arr || arr.length === 0) return undefined;
  return arr[from.pos - 1] ?? undefined;
};

const fromReferencesGroup = (from) =>
  from && from.group != null && from.mid == null;

const getAllowedThirdGroups = (from) => {
  const ref = String((from && from.group) || '');
  if (!ref) return [];
  return ref.length === 1 ? [ref] : ref.split('');
};

const normalizeGroupCode = (g) => String(g || '').trim().toLowerCase();

const buildBestThirdAssignmentsFallback = (matches = [], bestThirds = []) => {
  const rankedByGroup = {};
  (bestThirds || []).forEach((entry, rank) => {
    if (!entry || !entry.group) return;
    const group = normalizeGroupCode(entry.group);
    if (!group || rankedByGroup[group]) return;
    rankedByGroup[group] = { team: entry.team, rank };
  });

  const slots = [];
  const pushSlot = (match, side, from) => {
    if (!fromReferencesGroup(from) || from.pos !== 3) return;
    const allowed = getAllowedThirdGroups(from).filter((g) => rankedByGroup[g]);
    slots.push({ key: `${match.mid}:${side}`, allowed });
  };

  (matches || []).forEach((match) => {
    if (!match) return;
    if (match.round !== Round.Roundof16 && match.round !== Round.Roundof32) return;
    pushSlot(match, 'home', match.fromHome);
    pushSlot(match, 'away', match.fromAway);
  });

  const minRank = (allowed) =>
    allowed.reduce(
      (min, g) => Math.min(min, rankedByGroup[g] ? rankedByGroup[g].rank : Number.MAX_SAFE_INTEGER),
      Number.MAX_SAFE_INTEGER
    );

  const orderedSlots = slots.slice().sort(
    (a, b) => a.allowed.length - b.allowed.length || minRank(a.allowed) - minRank(b.allowed)
  );

  const assignedByKey = {};
  const usedGroups = new Set();

  const dfs = (idx) => {
    if (idx >= orderedSlots.length) return true;
    const slot = orderedSlots[idx];
    const choices = slot.allowed
      .filter((g) => !usedGroups.has(g))
      .sort((a, b) => rankedByGroup[a].rank - rankedByGroup[b].rank);

    for (const group of choices) {
      usedGroups.add(group);
      assignedByKey[slot.key] = rankedByGroup[group].team;
      if (dfs(idx + 1)) return true;
      usedGroups.delete(group);
      delete assignedByKey[slot.key];
    }
    return false;
  };

  dfs(0);
  return assignedByKey;
};

const buildBestThirdMatrixAssignments = (matches = [], bestThirds = []) => {
  const rankedByGroup = {};
  (bestThirds || []).forEach((entry, rank) => {
    if (!entry || !entry.group) return;
    const group = normalizeGroupCode(entry.group);
    if (!group || rankedByGroup[group]) return;
    rankedByGroup[group] = { team: entry.team, rank };
  });

  const qualifiedGroups = Object.keys(rankedByGroup).slice(0, 8);
  if (qualifiedGroups.length < 8) return null;

  const comboKey = qualifiedGroups.slice().sort().join('');
  const matrixRow = get(fifaBestThirdMatrix, ['byCombo', comboKey]);
  const assignmentsBySlot = get(matrixRow, 'assignments');
  if (!assignmentsBySlot) return null;

  const assignedByKey = {};
  const getThirdSlotKey = (match, side) => {
    const own = side === 'home' ? match.fromHome : match.fromAway;
    const opp = side === 'home' ? match.fromAway : match.fromHome;
    if (!fromReferencesGroup(own) || own.pos !== 3) return null;
    if (!fromReferencesGroup(opp) || opp.pos !== 1) return null;
    return `1${normalizeGroupCode(opp.group)}`;
  };

  (matches || []).forEach((match) => {
    if (!match) return;
    if (match.round !== Round.Roundof16 && match.round !== Round.Roundof32) return;

    ['home', 'away'].forEach((side) => {
      const slotKey = getThirdSlotKey(match, side);
      if (!slotKey) return;
      const assignedGroup = normalizeGroupCode(assignmentsBySlot[slotKey]);
      const ranked = rankedByGroup[assignedGroup];
      if (!ranked || !ranked.team) return;
      assignedByKey[`${match.mid}:${side}`] = ranked.team;
    });
  });

  return assignedByKey;
};

const buildBestThirdAssignments = (matches = [], bestThirds = []) =>
  buildBestThirdMatrixAssignments(matches, bestThirds) ||
  buildBestThirdAssignmentsFallback(matches, bestThirds);

const setTeamsFromGroups = (
  { positionsByGroup = {}, bestThirds = [] },
  { useResultOverrides = false } = {}
) => (matches) => {
  const resolveThirdByKey = buildBestThirdAssignments(matches, bestThirds);
  return map((match) => {
    if (match.round !== Round.Roundof16 && match.round !== Round.Roundof32) {
      return match;
    }
    if (!fromReferencesGroup(match.fromHome) && !fromReferencesGroup(match.fromAway)) {
      return match;
    }

    const resolveSeededTeam = (from, currentTeam, side) => {
      if (!fromReferencesGroup(from)) return currentTeam;
      if (useResultOverrides) {
        const overrideField = side === 'home' ? 'overrideTeamHome' : 'overrideTeamAway';
        const overridden = match[overrideField];
        if (typeof overridden === 'string' && overridden.trim()) {
          return overridden;
        }
      }
      if (from.pos === 3) {
        return resolveThirdByKey[`${match.mid}:${side}`] || currentTeam;
      }
      return getTeamFromGroup(positionsByGroup, from);
    };

    return {
      ...match,
      teamHome: resolveSeededTeam(match.fromHome, match.teamHome, 'home'),
      teamAway: resolveSeededTeam(match.fromAway, match.teamAway, 'away')
    };
  })(matches);
};

const setTeamsFromGroupsHybrid = (
  forecastQualifiers,
  realQualifiers,
  startRound
) => (matches) => {
  const isKnockoutSeededMatch = (match) =>
    match &&
    (match.round === Round.Roundof16 || match.round === Round.Roundof32) &&
    (fromReferencesGroup(match.fromHome) || fromReferencesGroup(match.fromAway));

  const isRealScopeMatch = (match) =>
    Boolean(startRound && match && isRoundAtOrAfter(match.round, startRound));

  const forecastScopeMatches = (matches || []).filter(
    (m) => isKnockoutSeededMatch(m) && !isRealScopeMatch(m)
  );
  const realScopeMatches = (matches || []).filter(
    (m) => isKnockoutSeededMatch(m) && isRealScopeMatch(m)
  );

  const resolveThirdByKeyForecast = buildBestThirdAssignments(
    forecastScopeMatches,
    (forecastQualifiers && forecastQualifiers.bestThirds) || []
  );
  const resolveThirdByKeyReal = buildBestThirdAssignments(
    realScopeMatches,
    (realQualifiers && realQualifiers.bestThirds) || []
  );

  return map((match) => {
    if (startRound && isRoundBefore(match.round, startRound)) {
      return match;
    }
    if (match.round !== Round.Roundof16 && match.round !== Round.Roundof32) {
      return match;
    }
    if (!fromReferencesGroup(match.fromHome) && !fromReferencesGroup(match.fromAway)) {
      return match;
    }

    const useReal = startRound && isRoundAtOrAfter(match.round, startRound);
    const { positionsByGroup = {} } = useReal
      ? realQualifiers
      : forecastQualifiers;
    const resolveThirdByKey = useReal
      ? resolveThirdByKeyReal
      : resolveThirdByKeyForecast;

    const resolveSeededTeam = (from, currentTeam, side) => {
      if (!fromReferencesGroup(from)) return currentTeam;
      if (from.pos === 3) {
        return resolveThirdByKey[`${match.mid}:${side}`] || currentTeam;
      }
      return getTeamFromGroup(positionsByGroup, from);
    };

    return {
      ...match,
      teamHome: resolveSeededTeam(match.fromHome, match.teamHome, 'home'),
      teamAway: resolveSeededTeam(match.fromAway, match.teamAway, 'away')
    };
  })(matches);
};

const normalizeResolvedTeam = (team) =>
  typeof team === 'string' && team ? team : undefined;

const moveBracketWinners = (matches) =>
  matches.reduce((result, match) => {
    const getTeam = resolveTeam(keyBy(MatchProps.MatchId, result));
    const seededFromGroupOnly =
      (fromReferencesGroup(match.fromHome) ||
        fromReferencesGroup(match.fromAway)) &&
      (match.round === Round.Roundof16 || match.round === Round.Roundof32);

    return [
      ...result,
      seededFromGroupOnly
        ? match
        : {
            ...match,
            teamHome: normalizeResolvedTeam(getTeam(match.fromHome)),
            teamAway: normalizeResolvedTeam(getTeam(match.fromAway))
          }
    ];
  }, []);

const transformBracket = (groupQualifiers) =>
  pipe(
    setTeamsFromGroups(groupQualifiers, { useResultOverrides: true }),
    moveBracketWinners,
    keyBy(MatchProps.MatchId)
  );

const transformForecastBracket = (
  forecastQualifiers,
  realQualifiers,
  { usesComodinHybrid, startRound }
) =>
  pipe(
    usesComodinHybrid
      ? setTeamsFromGroupsHybrid(forecastQualifiers, realQualifiers, startRound)
      : setTeamsFromGroups(forecastQualifiers, { useResultOverrides: false }),
    moveBracketWinners,
    keyBy(MatchProps.MatchId)
  );

const overlayRealKnockoutBeforeActivation = (startRound, realBracketKeyed) =>
  map((match) => {
    if (!startRound || !isRoundBefore(match.round, startRound)) return match;

    const real =
      realBracketKeyed[match.mid] ?? realBracketKeyed[String(match.mid)];
    if (!real || !hasResults(real)) return match;

    return {
      ...match,
      teamHome: real.teamHome,
      teamAway: real.teamAway,
      scoreHome: real.scoreHome,
      scoreAway: real.scoreAway,
      winner: real.winner,
    };
  });

export const selectBracketMatches = createSelector(
  [selectBracketStage, selectGroupQualifiers],
  (bracketMatches, groupQualifiers) => transformBracket(groupQualifiers)(bracketMatches)
);

export const selectBrackets = createSelector(
  [
    selectBracketStage,
    selectGroupQualifiers,
    prop('now'),
    prop('config'),
    prop('forecastMatches')
  ],
  (bracketMatches, groupQualifiers, now, config, forecastMatches) =>
    pipe(
      map(addIsToday(now)),
      mergeAssertions(forecastMatches || []),
      transformBracket(groupQualifiers),
      (keyed) => getBracketRounds(keyed, config)
    )(bracketMatches)
); // config drives WC2026 vs legacy bracket *shape* (via shouldUseWc2026BracketLayout)

const sameMid = (m, mid) => Number(m && m.mid) === Number(mid);

const forecastRowHasStoredPick = (row) =>
  Boolean(
    row &&
      (row.modificadoPorComodin ||
        row.scoreHome != null ||
        row.scoreAway != null ||
        (row.winner != null && row.winner !== '') ||
        (row.teamHome && row.teamAway))
  );

const overlayComodinBracketForecast = (
  forecast,
  midToRound,
  startRound,
  wildcardActive = false
) =>
  mapValues((match) => {
    const round = midToRound[match.mid];

    // Rondas anteriores a la activación: bloqueadas para editar. Si el partido
    // real ya se jugó, overlayRealKnockoutBeforeActivation dejó el resultado;
    // si no, aplicamos el pronóstico guardado del usuario (solo lectura).
    if (startRound && round && isRoundBefore(round, startRound)) {
      const row = find(forecast, (wm) => sameMid(wm, match.mid));
      if (!row || !forecastRowHasStoredPick(row) || hasResults(match)) {
        return match;
      }
      return {
        ...match,
        teamHome: row.teamHome ?? match.teamHome,
        teamAway: row.teamAway ?? match.teamAway,
        scoreHome: row.scoreHome ?? match.scoreHome,
        scoreAway: row.scoreAway ?? match.scoreAway,
        winner: row.winner ?? match.winner,
      };
    }

    const row = find(forecast, (wm) => sameMid(wm, match.mid));
    if (!row) return match;

    if (forecastRowHasStoredPick(row)) {
      return {
        ...match,
        teamHome: row.teamHome ?? match.teamHome,
        teamAway: row.teamAway ?? match.teamAway,
        scoreHome: row.scoreHome ?? match.scoreHome,
        scoreAway: row.scoreAway ?? match.scoreAway,
        winner: row.winner ?? match.winner,
      };
    }

    if (
      startRound &&
      round &&
      isRoundAfter(round, startRound) &&
      !wildcardActive
    ) {
      return match;
    }

    return match;
  });

const sortMatchesByRoundOrder = (matches) =>
  [...matches].sort((a, b) => {
    const roundDiff =
      ROUND_ORDER.indexOf(a.round) - ROUND_ORDER.indexOf(b.round);
    if (roundDiff !== 0) return roundDiff;
    return (a.mid || 0) - (b.mid || 0);
  });

const propagateBracketWinnersKeyed = (keyed) => {
  if (!keyed || typeof keyed !== 'object') return keyed;
  const propagated = moveBracketWinners(
    sortMatchesByRoundOrder(Object.values(keyed))
  );
  return keyBy(MatchProps.MatchId, propagated);
};

const applyKnockoutScopeEditable = (scopeActive, startRound) => (keyed) => {
  if (!scopeActive || !startRound) return keyed;
  return mapValues((match) => {
    if (!match.round) return match;
    return isRoundAtOrAfter(match.round, startRound)
      ? { ...match, editable: true }
      : { ...match, editable: false };
  })(keyed);
};

const buildForecastBracketKeyed = (
  bracketMatches,
  groupQualifiersReal,
  groupQualifiersForecast,
  forecast,
  now,
  wildcardMode,
  knockoutEditPermitEnabled,
  hasRoundOf32,
  realBracketKeyed
) => {
  const wildcardActive = Boolean(wildcardMode && wildcardMode.enabled);
  const permitActive = Boolean(knockoutEditPermitEnabled);
  const scopeActive = wildcardActive || permitActive;
  const midToRound = bracketMatches.reduce(
    (all, match) =>
      match && typeof match.mid === 'number'
        ? { ...all, [match.mid]: match.round }
        : all,
    {}
  );
  const comodinStartRound = getComodinStartRound(forecast, midToRound);
  const startRound =
    wildcardActive && wildcardMode.faseActivacion
      ? getStartRoundForFase(wildcardMode.faseActivacion, hasRoundOf32)
      : permitActive
        ? getStartRoundForFase('16avos', hasRoundOf32)
      : comodinStartRound;
  const usesComodinBracketLogic = Boolean(
    wildcardActive || (!permitActive && comodinStartRound)
  );
  const forecastForTransform = wildcardActive
    ? stripForecastTeamsForPropagation(
        forecast,
        midToRound,
        startRound,
        usesComodinBracketLogic
      )
    : stripStaleForecastAfterComodin(
        forecast,
        midToRound,
        startRound,
        usesComodinBracketLogic
      );
  const overlayForecast = usesComodinBracketLogic
    ? overlayComodinBracketForecast(
        forecast || [],
        midToRound,
        startRound,
        wildcardActive
      )
    : mergeMatchesKeyed(forecast || []);
  const overlayBeforeRound = usesComodinBracketLogic ? startRound : null;

  return pipe(
    ...(now ? [map(addIsToday(now))] : []),
    map(
      omit([
        MatchProps.TeamHome,
        MatchProps.TeamAway,
        MatchProps.ScoreHome,
        MatchProps.ScoreAway,
        MatchProps.Winner
      ])
    ),
    mergeMatches(forecastForTransform),
    ...(overlayBeforeRound
      ? [overlayRealKnockoutBeforeActivation(overlayBeforeRound, realBracketKeyed)]
      : []),
    transformForecastBracket(groupQualifiersForecast, groupQualifiersReal, {
      usesComodinHybrid: usesComodinBracketLogic,
      startRound,
    }),
    overlayForecast,
    ...(usesComodinBracketLogic || permitActive
      ? [propagateBracketWinnersKeyed]
      : []),
    applyKnockoutScopeEditable(scopeActive, startRound)
  )(bracketMatches);
};

export const getWildcardStartRound = (state) => {
  if (!isWildcardModeEnabled(state)) return null;
  const faseActivacion = get(state, 'wildcardMode.faseActivacion');
  if (!faseActivacion) return null;
  return getStartRoundForFase(faseActivacion, selectUsesWc2026Bracket(state));
};

export const isKnockoutMatchEditableInWildcard = (state, match) => {
  if (!match?.round) return false;
  const startRound = getWildcardStartRound(state);
  if (!startRound) return false;
  return isRoundInWildcardScope(match.round, startRound);
};

export const getKnockoutEditPermitStartRound = (state) => {
  if (!isKnockoutEditPermitEnabled(state)) return null;
  return getStartRoundForFase('16avos', selectUsesWc2026Bracket(state));
};

export const isKnockoutMatchEditableInPermit = (state, match) => {
  if (!match?.round) return false;
  const startRound = getKnockoutEditPermitStartRound(state);
  if (!startRound) return false;
  return isRoundInWildcardScope(match.round, startRound);
};

export const isKnockoutMatchEditable = (state, match) => {
  if (isWildcardModeEnabled(state)) {
    return isKnockoutMatchEditableInWildcard(state, match);
  }
  if (isKnockoutEditPermitEnabled(state)) {
    return isKnockoutMatchEditableInPermit(state, match);
  }
  return Boolean(match?.editable);
};

export const canEditGroupsInForecast = (state) =>
  isForecastView(state) &&
  canEditForecast(state) &&
  !isWildcardModeEnabled(state) &&
  !isKnockoutEditPermitEnabled(state);

export const selectBracketForecastMatches = createSelector(
  [
    selectBracketStage,
    selectGroupQualifiers,
    selectGroupQualifiersForecast,
    prop('forecastMatches'),
    prop('now'),
    prop('wildcardMode'),
    isKnockoutEditPermitEnabled,
    selectBracketMatches,
    prop('config'),
  ],
  (
    bracketMatches,
    groupQualifiersReal,
    groupQualifiersForecast,
    forecast,
    now,
    wildcardMode,
    knockoutEditPermitEnabled,
    bracketKeyed,
    config
  ) =>
    buildForecastBracketKeyed(
      bracketMatches,
      groupQualifiersReal,
      groupQualifiersForecast,
      forecast,
      now,
      wildcardMode,
      knockoutEditPermitEnabled,
      shouldUseWc2026BracketLayout(bracketKeyed, config),
      bracketKeyed
    )
);

// Vista de pronóstico: equipos del forecast salvo en modo comodín (desde fase activa)
export const selectBracketsForecast = createSelector(
  [
    selectBracketForecastMatches,
    prop('config')
  ],
  (keyed, config) => getBracketRounds(keyed, config)
);

export const selectBracketsForecastNoRounds = createSelector(
  [
    selectBracketStage,
    selectGroupQualifiers,
    selectGroupQualifiersForecast,
    prop('forecastMatches'),
    prop('wildcardMode'),
    isKnockoutEditPermitEnabled,
    selectBracketMatches,
    prop('config'),
  ],
  (
    bracketMatches,
    groupQualifiersReal,
    groupQualifiersForecast,
    forecast,
    wildcardMode,
    knockoutEditPermitEnabled,
    bracketKeyed,
    config
  ) =>
    buildForecastBracketKeyed(
      bracketMatches,
      groupQualifiersReal,
      groupQualifiersForecast,
      forecast,
      null,
      wildcardMode,
      knockoutEditPermitEnabled,
      shouldUseWc2026BracketLayout(bracketKeyed, config),
      bracketKeyed
    )
);

export const selectGroupWinnersEditable = createSelector(
  selectGroupMatches,
  groups =>
    Object.keys(groups).reduce(
      (all, group) => ({
        ...all,
        [group]: some({ editable: true }, groups[group])
      }),
      groups
    )
);

export const isSaving = createSelector(
  prop('savingForecast'),
  isSaving => isSaving
);

export const isForecastDataDirty = createSelector(
  [prop('forecastMatches'), prop('forecastGroups')],
  (...args) => pipe(map(some({ dirty: true })), some(Boolean))(args)
);

export const isForecastDirty = createSelector(
  [isForecastDataDirty, isPlayerDirty],
  (...args) => some(Boolean)(args)
);

export const isCleanExit = createSelector(
  [isEditingForecast, isForecastDirty],
  (isEditing, isDirty) => (isEditing && isDirty ? false : true)
);

export const selectUsesWc2026Bracket = createSelector(
  [selectBracketMatches, (state) => state.config],
  (matches, config) => shouldUseWc2026BracketLayout(matches, config)
);

export const matchesByDate = createSelector(
  selectMatches,
  selectBracketMatches,
  (matches, bracketMatches) => {
    return pipe(
      mergeMatches(bracketMatches),
      groupBy((match) => match[MatchProps.Datetime].slice(0, 10))
    )(matches);
  }
);

export const bracketMatchesByDate = createSelector(
  [selectBracketMatches, prop('location.payload.round')],
  (bracketMatches = [], stage) => {
      const embedRoundToMatchRound = {
        '16': ['roundof32'],
        '8': ['roundof16'],
        '4': ['quarter'],
        '2': ['semi'],
        '1': ['final', 'third']
      };
      return pipe(
        pickBy((match) => stage && embedRoundToMatchRound[stage] ? 
        embedRoundToMatchRound[stage].includes(match.round) : 
          true
        ),
        groupBy((match) => match[MatchProps.Datetime].slice(0, 10))
      )(bracketMatches);
  }
);

export const selectTodayMatches = createSelector(
  [prop('now'), selectMatches, selectBracketMatches],
  (now, matches, bracketMatches) => {
    return pipe(
      filter((m) => moment(m[MatchProps.Datetime]).isSame(now, 'day')),
      mergeMatches(bracketMatches),
    )(matches)
  }
);